import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { TFunction } from 'i18next';
import {
    PaymentHistoryEvent,
    PaymentHistoryResponse,
    useLazyGetPaymentHistoryQuery,
} from '../../../api/booking/paymentHistoryApiSlice';
import { STATION_TIME_ZONE } from '../../../utils/stationTime';

/**
 * On-demand payment history for a booking (#2002, step 5 of #1431).
 *
 * ── Why on demand ────────────────────────────────────────────────────────
 *
 * The payment STATUS stays front-and-centre in the existing column; the
 * history is the ledger's full trail and is one click away rather than
 * clutter. It is fetched lazily for the same reason — most bookings are opened
 * to check a date, not to audit money.
 *
 * ── Why the raw event type is always shown ───────────────────────────────
 *
 * The ledger is provider-agnostic and append-only: a bank-transfer adapter or
 * a new Stripe lifecycle step can produce an `eventType` this build has never
 * heard of. Unknown types render from the raw value rather than disappearing —
 * a money event nobody can see is worse than one with an ugly label.
 *
 * New surface, so: Tailwind design system, theme tokens only, no MUI.
 */

/** i18n keys exist for the types we know; anything else falls back to raw. */
const KNOWN_TYPES = new Set([
    'charge_succeeded',
    'charge_failed',
    'charge_pending',
    'refund',
    'deposit_authorized',
    'deposit_captured',
    'deposit_released',
    'dispute_opened',
    'dispute_won',
    'dispute_lost',
    'checkout_expired',
]);

const formatAmount = (
    amount: number | null,
    currency: string | null,
    fallback: string,
): string => {
    if (amount === null || amount === undefined) return fallback;
    return `${amount.toFixed(2)} ${currency ?? ''}`.trim();
};

const formatMoment = (iso: string): string => {
    const date = new Date(iso);
    return Number.isNaN(date.getTime()) ? iso : date.toLocaleString(undefined, { timeZone: STATION_TIME_ZONE });
};

interface Props {
    orderId: string;
}


/** `YYYY-MM-DD` → `DD.MM.YYYY`; a calendar day, so no time zone applies. */
const dayOf = (ymd: string): string => ymd.split('-').reverse().join('.');

export const emptyText = (data: PaymentHistoryResponse, t: TFunction): string =>
    !data.authoritative && data.authoritativeFrom
        ? t('booking_details.payment_history.empty_before_ledger', { date: dayOf(data.authoritativeFrom) })
        : t('booking_details.payment_history.empty');

const PaymentHistorySection: React.FC<Props> = ({ orderId }) => {
    const { t } = useTranslation();
    const [open, setOpen] = useState(false);
    const [fetchHistory, { data, isFetching, isError }] =
        useLazyGetPaymentHistoryQuery();

    const notAvailable = t('common.not_applicable');

    const toggle = () => {
        const next = !open;
        setOpen(next);
        // Fetched on first open only; RTK Query serves the cache afterwards.
        if (next && !data) fetchHistory({ orderId });
    };

    const labelFor = (event: PaymentHistoryEvent): string =>
        KNOWN_TYPES.has(event.eventType)
            ? t(`booking_details.payment_history.event_type.${event.eventType}`)
            : event.eventType;

    return (
        <div className={'bg-text-contrast rounded-[5px] shadow-sm p-4'}>
            <button
                type={'button'}
                onClick={toggle}
                aria-expanded={open}
                className={'w-full flex items-center justify-between text-left'}
            >
                <span className={'text-sm font-semibold text-foreground'}>
                    {t('booking_details.payment_history.title')}
                </span>
                <span className={'text-sm text-foreground/60'}>
                    {open
                        ? t('booking_details.payment_history.hide')
                        : t('booking_details.payment_history.show')}
                </span>
            </button>

            {open && (
                <div className={'mt-4'}>
                    {isFetching && (
                        <p className={'text-sm text-foreground/60'}>
                            {t('common.loading')}
                        </p>
                    )}

                    {/* An error must not read as "no payment events" — a
                        failure to load and an empty ledger look identical to a
                        user otherwise, and that is exactly how a broken money
                        path stays unreported (#1579). */}
                    {isError && !isFetching && (
                        <p className={'text-sm text-foreground'}>
                            {t('booking_details.payment_history.load_failed')}
                        </p>
                    )}

                    {!isFetching && !isError && data && (
                        <>
                            <dl className={'grid grid-cols-2 gap-x-6 gap-y-2 mb-4 text-sm sm:grid-cols-4'}>
                                {[
                                    ['gross', data.summary.settled],
                                    ['refunded', data.summary.refunded],
                                    ['disputed', data.summary.disputed],
                                    ['net', data.summary.net],
                                ].map(([key, value]) => (
                                    <div key={key as string}>
                                        <dt className={'text-foreground/60'}>
                                            {t(
                                                `booking_details.payment_history.totals.${key}`,
                                            )}
                                        </dt>
                                        {/* Gross, refunds and net as three
                                            numbers, never one blended figure. */}
                                        <dd className={'text-foreground font-medium'}>
                                            {(value as number).toFixed(2)}
                                        </dd>
                                    </div>
                                ))}
                            </dl>

                            {data.events.length === 0 ? (
                                <p className={'text-sm text-foreground/60'}>
                                    {/* Says "nothing recorded", not "never
                                        paid" — and for a booking from before
                                        the ledger was the tenant's record, says
                                        where its payments are instead. */}
                                    {emptyText(data, t)}
                                </p>
                            ) : (
                                <div className={'overflow-x-auto'}>
                                    <table className={'w-full text-sm'}>
                                        <thead>
                                            <tr className={'text-foreground/60 font-medium text-left'}>
                                                <th className={'py-2 pr-4 font-medium'}>
                                                    {t('booking_details.payment_history.columns.when')}
                                                </th>
                                                <th className={'py-2 pr-4 font-medium'}>
                                                    {t('booking_details.payment_history.columns.event')}
                                                </th>
                                                <th className={'py-2 pr-4 font-medium'}>
                                                    {t('booking_details.payment_history.columns.amount')}
                                                </th>
                                                <th className={'py-2 font-medium'}>
                                                    {t('booking_details.payment_history.columns.reference')}
                                                </th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {data.events.map((event) => (
                                                <tr
                                                    key={event.id}
                                                    className={'border-t border-foreground/5'}
                                                >
                                                    <td className={'py-2 pr-4 text-foreground whitespace-nowrap'}>
                                                        {formatMoment(event.occurredAt)}
                                                    </td>
                                                    <td className={'py-2 pr-4 text-foreground'}>
                                                        {labelFor(event)}
                                                    </td>
                                                    <td className={'py-2 pr-4 text-foreground whitespace-nowrap'}>
                                                        {formatAmount(
                                                            event.amount,
                                                            event.currency,
                                                            notAvailable,
                                                        )}
                                                    </td>
                                                    {/* The provider id is what
                                                        reconciles a line 1:1
                                                        against Stripe, so it is
                                                        shown rather than hidden
                                                        as an implementation
                                                        detail. */}
                                                    <td className={'py-2 text-foreground/60 break-all'}>
                                                        {event.providerReference || notAvailable}
                                                    </td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>
                                </div>
                            )}
                        </>
                    )}
                </div>
            )}
        </div>
    );
};

export default PaymentHistorySection;
